import { useMemo } from "react";

import useLocations from "#/hooks/data/pos/use-locations";
import { useDspLocationsQuery } from "#/hooks/queries/use-dsp-locations-query";

export interface LocationOption {
  value: string;
  label: string;
}

/**
 * Single source of truth for "location as a select option" data. Merchant
 * locations and DSP-connected locations are two separate queries
 * (`useLocations` / `useDspLocationsQuery`) that every locations-picker call
 * site otherwise re-derived independently — this hook is the parent-level
 * fetch that presentational selectors (components/selectors/locations-select)
 * receive their `options` from as props.
 */
export function useLocationOptions() {
  const { data: locations, loading } = useLocations();
  const { data: dspLocationsData } = useDspLocationsQuery();

  const options = useMemo<LocationOption[]>(
    () => locations.map((l) => ({ value: l._id, label: l.name })),
    [locations],
  );

  const dspConnectedOptions = useMemo<LocationOption[]>(() => {
    const dspLocationIds = dspLocationsData?.locations?.map(
      (l) => l?.location_id,
    );
    return locations
      .filter((l) => dspLocationIds?.includes?.(l?._id))
      .map((l) => ({ value: l._id, label: l.name }));
  }, [locations, dspLocationsData]);

  return { options, dspConnectedOptions, loading };
}

export default useLocationOptions;
